import { getCurrencySymbol } from '@vy/services';
import { COMMITMENT_TYPES, type CommitmentInput as CommitmentInputType, type CommitmentType } from '@vy/types';
import { isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import { InputAdornment } from '@mui/material';
import { get } from 'lodash';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { LabeledField, VyStack, VyTextField, VyToggleButton, VyToggleButtonGroup } from '@vy/ui-design-system';
import { useChargeContext } from '../../charge-context';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';

export const CommitmentInput: React.FC = () => {
  const { itemIndex: productIndex, currency } = useChargeContext();
  const { watch, setValue, setError, clearErrors, formState } = useFormContext<PlanInputForm>();
  const { errors } = formState;

  const commitment = watch(`usageProducts.${productIndex}.commitment`);
  const commitmentAmount = watch(`usageProducts.${productIndex}.commitment.amount`);
  const commitmentType = watch(`usageProducts.${productIndex}.commitment.type`);
  const originalCommitmentAmount = watch(`usageProducts.${productIndex}._originalCommitmentAmount`);

  const hasExistingCommitment =
    isNotNullOrUndefined(originalCommitmentAmount) && originalCommitmentAmount > 0;

  const amountFieldName = `usageProducts.${productIndex}.commitment.amount` as const;
  const typeFieldName = `usageProducts.${productIndex}.commitment.type` as const;

  // Unset commitment once both amount and type are cleared.
  useEffect(() => {
    if (
      isNotNullOrUndefined(commitment) &&
      isNullOrUndefined(commitmentAmount) &&
      isNullOrUndefined(commitmentType)
    ) {
      setValue(`usageProducts.${productIndex}.commitment`, undefined);
    }
  }, [commitment, commitmentAmount, commitmentType, productIndex, setValue]);

  const handleAmountChange = (rawValue: string) => {
    const value = Number(rawValue);

    if (!(value > 0)) {
      clearErrors(amountFieldName);
      setValue(`usageProducts.${productIndex}.commitment`, undefined);
      return;
    }

    if (hasExistingCommitment && value < (originalCommitmentAmount as number)) {
      setError(amountFieldName, {
        type: 'custom',
        message: `Minimum: ${originalCommitmentAmount}`,
      });
    } else {
      clearErrors(amountFieldName);
    }

    const updatedCommitment: CommitmentInputType = {
      amount: value,
      type: commitmentType ?? COMMITMENT_TYPES.Units,
      consumption: commitment?.consumption ?? 0,
      autoRefillEnabled: commitment?.autoRefillEnabled ?? false,
      replenishmentInterval: commitment?.replenishmentInterval,
    };
    setValue(`usageProducts.${productIndex}.commitment`, updatedCommitment);
  };

  const handleTypeChange = (_event: React.MouseEvent<HTMLElement>, nextType: CommitmentType | null) => {
    if (isNullOrUndefined(nextType)) {
      return;
    }
    setValue(typeFieldName, nextType);
  };

  const amountError = get(errors, amountFieldName)?.message as string | undefined;
  const amountHelperText =
    amountError ??
    (hasExistingCommitment
      ? `Minimum: ${originalCommitmentAmount}`
      : 'Commitment is the minimum quantity the customer agrees to buy over the plan term');

  const isCurrencyType = commitmentType === COMMITMENT_TYPES.Price;
  const currencyAdornment = isCurrencyType ? (
    <InputAdornment position="start">{getCurrencySymbol(currency)}</InputAdornment>
  ) : undefined;

  return (
    <LabeledField label="Commitment" helperText={amountHelperText} error={Boolean(amountError)}>
      <VyStack direction="row" sx={{ gap: 1, alignItems: 'stretch' }}>
        <VyToggleButtonGroup
          value={commitmentType ?? COMMITMENT_TYPES.Units}
          onChange={handleTypeChange}
          data-vy="usage-charge-drawer-commitment-type-toggle"
          sx={{ flexShrink: 0, '& .MuiToggleButton-root': { paddingBlock: 0 } }}
        >
          <VyToggleButton
            value={COMMITMENT_TYPES.Price}
            data-vy="usage-charge-drawer-commitment-type-price"
          >
            {currency}
          </VyToggleButton>
          <VyToggleButton
            value={COMMITMENT_TYPES.Units}
            data-vy="usage-charge-drawer-commitment-type-units"
          >
            Units
          </VyToggleButton>
        </VyToggleButtonGroup>

        <VyStack sx={{ flex: 1, minWidth: 0 }}>
          <VyTextField
            value={commitmentAmount ?? ''}
            onChange={(event) => handleAmountChange(event.target.value)}
            placeholder="Amount"
            type="number"
            fullWidth
            error={Boolean(amountError)}
            slotProps={currencyAdornment ? { input: { startAdornment: currencyAdornment } } : undefined}
            data-vy="usage-charge-drawer-commitment-amount"
          />
        </VyStack>
      </VyStack>
    </LabeledField>
  );
};
